/**
 * Listing Detail Tabs Management Page
 * Admin UI to configure Overview, Performance, Running Costs tabs
 */
import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../../components/ui/card';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import { Label } from '../../../components/ui/label';
import { Badge } from '../../../components/ui/badge';
import { Switch } from '../../../components/ui/switch';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from '../../../components/ui/dialog';
import {
  ArrowLeft, Save, Plus, Trash2, Edit, GripVertical,
  Car, Gauge, Wallet, Fuel, Calendar, Settings2,
  Zap, Leaf, Shield, DollarSign, Eye, RefreshCw
} from 'lucide-react';
import { useToast } from '../../../hooks/use-toast';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Available icons for tabs
const AVAILABLE_ICONS = [
  { key: 'car', label: 'Car', Icon: Car },
  { key: 'gauge', label: 'Gauge', Icon: Gauge },
  { key: 'wallet', label: 'Wallet', Icon: Wallet },
  { key: 'fuel', label: 'Fuel', Icon: Fuel },
  { key: 'calendar', label: 'Calendar', Icon: Calendar },
  { key: 'settings', label: 'Settings', Icon: Settings2 },
  { key: 'zap', label: 'Performance', Icon: Zap },
  { key: 'leaf', label: 'Eco', Icon: Leaf },
  { key: 'shield', label: 'Safety', Icon: Shield },
  { key: 'dollar', label: 'Cost', Icon: DollarSign }
];

// Field types
const FIELD_TYPES = [
  { value: 'text', label: 'Text' },
  { value: 'number', label: 'Number' },
  { value: 'mileage', label: 'Mileage (with unit)' },
  { value: 'currency', label: 'Currency' },
  { value: 'fuel', label: 'Fuel Economy' },
  { value: 'date', label: 'Date' }
];

// Default tab configurations
const DEFAULT_TABS = {
  overview: {
    tab_key: 'overview',
    name: 'Overview',
    icon: 'car',
    enabled: true,
    order: 1,
    fields: [
      { key: 'year', label: 'Year', type: 'text' },
      { key: 'colour', label: 'Colour', type: 'text' },
      { key: 'mileage', label: 'Mileage', type: 'mileage' },
      { key: 'seats', label: 'Seats', type: 'number' },
      { key: 'body_type', label: 'Body Type', type: 'text' },
      { key: 'doors', label: 'Doors', type: 'number' },
      { key: 'transmission', label: 'Transmission', type: 'text' },
      { key: 'luggage_capacity', label: 'Luggage Capacity (seats up)', type: 'text' }
    ]
  },
  performance: {
    tab_key: 'performance',
    name: 'Performance',
    icon: 'gauge',
    enabled: true,
    order: 2,
    fields: [
      { key: 'engine_size', label: 'Engine Size', type: 'text' },
      { key: 'engine_power', label: 'Engine Power', type: 'text' },
      { key: 'torque', label: 'Torque', type: 'text' },
      { key: 'acceleration', label: '0-60 mph', type: 'text' },
      { key: 'top_speed', label: 'Top Speed', type: 'text' },
      { key: 'drive_type', label: 'Drive Type', type: 'text' },
      { key: 'fuel_type', label: 'Fuel Type', type: 'text' },
      { key: 'cylinders', label: 'Cylinders', type: 'text' }
    ]
  },
  running_costs: {
    tab_key: 'running_costs',
    name: 'Running Costs',
    icon: 'wallet',
    enabled: true,
    order: 3,
    fields: [
      { key: 'fuel_economy', label: 'Better fuel economy', type: 'fuel' },
      { key: 'insurance_group', label: 'Lower insurance group', type: 'text' },
      { key: 'tax_per_year', label: 'Tax per year', type: 'currency' },
      { key: 'co2_emissions', label: 'CO₂ emissions', type: 'text' },
      { key: 'euro_emissions', label: 'Euro Emissions Standard', type: 'text' },
      { key: 'service_cost', label: 'Annual Service Cost', type: 'currency' }
    ]
  }
};

const ListingTabsManagementPage = () => {
  const { toast } = useToast();
  const [tabsConfig, setTabsConfig] = useState(DEFAULT_TABS);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  
  // Dialog states
  const [editFieldDialog, setEditFieldDialog] = useState({ open: false, tabKey: null, fieldIndex: null, field: null });
  const [addFieldDialog, setAddFieldDialog] = useState({ open: false, tabKey: null });
  const [newField, setNewField] = useState({ key: '', label: '', type: 'text' });

  // Fetch configuration
  const fetchConfig = useCallback(async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API_URL}/api/admin/vehicles/listing-tabs/config`);
      if (response.data.success && response.data.tabs) {
        setTabsConfig(response.data.tabs);
      }
    } catch (error) {
      console.error('Failed to fetch config:', error);
      toast({
        title: 'Info',
        description: 'Using default configuration',
        variant: 'default'
      });
    } finally {
      setLoading(false);
    }
  }, [toast]);

  useEffect(() => {
    fetchConfig();
  }, [fetchConfig]);

  // Toggle tab enabled
  const handleToggleTab = async (tabKey, enabled) => {
    try {
      await axios.patch(
        `${API_URL}/api/admin/vehicles/listing-tabs/${tabKey}?admin_id=admin`,
        { enabled }
      );
      
      setTabsConfig(prev => ({
        ...prev,
        [tabKey]: { ...prev[tabKey], enabled }
      }));
      
      toast({
        title: 'Success',
        description: `${tabsConfig[tabKey].name} tab ${enabled ? 'enabled' : 'disabled'}`
      });
    } catch (error) {
      console.error('Failed to toggle tab:', error);
      toast({
        title: 'Error',
        description: 'Failed to update tab',
        variant: 'destructive'
      });
    }
  };

  // Save all configuration
  const handleSaveAll = async () => {
    setSaving(true);
    try {
      await axios.post(
        `${API_URL}/api/admin/vehicles/listing-tabs/config?admin_id=admin`,
        { tabs: tabsConfig }
      );
      
      toast({
        title: 'Success',
        description: 'Configuration saved successfully'
      });
    } catch (error) {
      console.error('Failed to save config:', error);
      toast({
        title: 'Error',
        description: 'Failed to save configuration',
        variant: 'destructive'
      });
    } finally {
      setSaving(false);
    }
  };

  // Add field to tab
  const handleAddField = (tabKey) => {
    if (!newField.key || !newField.label) {
      toast({
        title: 'Error',
        description: 'Field key and label are required',
        variant: 'destructive'
      });
      return;
    }
    
    setTabsConfig(prev => ({
      ...prev,
      [tabKey]: {
        ...prev[tabKey],
        fields: [...prev[tabKey].fields, { ...newField }]
      }
    }));
    
    setAddFieldDialog({ open: false, tabKey: null });
    setNewField({ key: '', label: '', type: 'text' });
    
    toast({
      title: 'Success',
      description: 'Field added. Remember to save changes.'
    });
  };

  // Update field
  const handleUpdateField = () => {
    const { tabKey, fieldIndex, field } = editFieldDialog;
    
    setTabsConfig(prev => {
      const newFields = [...prev[tabKey].fields];
      newFields[fieldIndex] = field;
      return {
        ...prev,
        [tabKey]: { ...prev[tabKey], fields: newFields }
      };
    });
    
    setEditFieldDialog({ open: false, tabKey: null, fieldIndex: null, field: null });
    toast({
      title: 'Success',
      description: 'Field updated. Remember to save changes.'
    });
  };

  // Delete field
  const handleDeleteField = (tabKey, fieldIndex) => {
    setTabsConfig(prev => ({
      ...prev,
      [tabKey]: {
        ...prev[tabKey],
        fields: prev[tabKey].fields.filter((_, i) => i !== fieldIndex)
      }
    }));
    
    toast({
      title: 'Success',
      description: 'Field removed. Remember to save changes.'
    });
  };

  // Update tab name/icon
  const handleUpdateTab = (tabKey, updates) => {
    setTabsConfig(prev => ({
      ...prev,
      [tabKey]: { ...prev[tabKey], ...updates }
    }));
  };

  // Get icon component
  const getIcon = (iconKey) => {
    const iconConfig = AVAILABLE_ICONS.find(i => i.key === iconKey);
    return iconConfig?.Icon || Car;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <RefreshCw className="h-8 w-8 animate-spin text-gray-400" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div className="flex items-center gap-4">
          <Link to="/admin/vehicles">
            <Button variant="ghost" size="sm">
              <ArrowLeft className="h-4 w-4 mr-2" />
              Back
            </Button>
          </Link>
          <div>
            <h1 className="text-2xl font-bold flex items-center gap-2">
              <Eye className="h-6 w-6 text-purple-600" />
              Listing Detail Tabs
            </h1>
            <p className="text-gray-500">Configure Overview, Performance, Running Costs tabs for vehicle listings</p>
          </div>
        </div>
        <Button onClick={handleSaveAll} disabled={saving}>
          {saving ? (
            <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
          ) : (
            <Save className="h-4 w-4 mr-2" />
          )}
          Save All Changes
        </Button>
      </div>

      {/* Preview Info */}
      <Card className="bg-gradient-to-r from-purple-50 to-blue-50 border-purple-200">
        <CardContent className="pt-6">
          <div className="flex items-start gap-4">
            <Eye className="h-8 w-8 text-purple-500" />
            <div>
              <h3 className="font-semibold text-purple-900">How it works</h3>
              <p className="text-sm text-purple-700">
                These tabs appear on individual vehicle listing pages. Each tab shows specific vehicle information 
                when available. You can enable/disable tabs, edit field labels, and add custom fields.
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Tabs Configuration */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {Object.values(tabsConfig).sort((a, b) => a.order - b.order).map(tab => {
          const IconComponent = getIcon(tab.icon);
          
          return (
            <Card key={tab.tab_key} className={!tab.enabled ? 'opacity-60' : ''}>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className={`p-2 rounded-lg ${tab.enabled ? 'bg-green-100 text-green-600' : 'bg-gray-100 text-gray-400'}`}>
                      <IconComponent className="h-5 w-5" />
                    </div>
                    <div>
                      <CardTitle className="text-lg">{tab.name}</CardTitle>
                      <CardDescription>Order: {tab.order}</CardDescription>
                    </div>
                  </div>
                  <Switch
                    checked={tab.enabled}
                    onCheckedChange={(checked) => handleToggleTab(tab.tab_key, checked)}
                  />
                </div>
              </CardHeader>
              <CardContent className="space-y-4">
                {/* Tab Settings */}
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label className="text-xs">Tab Name</Label>
                    <Input
                      value={tab.name}
                      onChange={(e) => handleUpdateTab(tab.tab_key, { name: e.target.value })}
                      className="h-8 text-sm"
                    />
                  </div>
                  <div>
                    <Label className="text-xs">Icon</Label>
                    <Select
                      value={tab.icon}
                      onValueChange={(value) => handleUpdateTab(tab.tab_key, { icon: value })}
                    >
                      <SelectTrigger className="h-8">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {AVAILABLE_ICONS.map(icon => (
                          <SelectItem key={icon.key} value={icon.key}>
                            <div className="flex items-center gap-2">
                              <icon.Icon className="h-4 w-4" />
                              {icon.label}
                            </div>
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                {/* Fields List */}
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <Label className="text-xs font-semibold">Fields ({tab.fields.length})</Label>
                    <Button
                      size="sm"
                      variant="outline"
                      className="h-7 text-xs"
                      onClick={() => setAddFieldDialog({ open: true, tabKey: tab.tab_key })}
                    >
                      <Plus className="h-3 w-3 mr-1" />
                      Add
                    </Button>
                  </div>
                  
                  <div className="space-y-1 max-h-64 overflow-y-auto">
                    {tab.fields.map((field, index) => (
                      <div
                        key={field.key}
                        className="flex items-center justify-between p-2 bg-gray-50 rounded text-sm group"
                      >
                        <div className="flex items-center gap-2">
                          <GripVertical className="h-3 w-3 text-gray-300" />
                          <span className="font-medium">{field.label}</span>
                          <Badge variant="outline" className="text-xs">
                            {field.type}
                          </Badge>
                        </div>
                        <div className="flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                          <Button
                            size="icon"
                            variant="ghost"
                            className="h-6 w-6"
                            onClick={() => setEditFieldDialog({ 
                              open: true, 
                              tabKey: tab.tab_key, 
                              fieldIndex: index,
                              field: { ...field }
                            })}
                          >
                            <Edit className="h-3 w-3" />
                          </Button>
                          <Button
                            size="icon"
                            variant="ghost"
                            className="h-6 w-6 text-red-500 hover:text-red-600"
                            onClick={() => handleDeleteField(tab.tab_key, index)}
                          >
                            <Trash2 className="h-3 w-3" />
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* Edit Field Dialog */}
      <Dialog open={editFieldDialog.open} onOpenChange={(open) => !open && setEditFieldDialog({ open: false, tabKey: null, fieldIndex: null, field: null })}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Edit Field</DialogTitle>
            <DialogDescription>Update the field configuration</DialogDescription>
          </DialogHeader>
          {editFieldDialog.field && (
            <div className="space-y-4 py-4">
              <div>
                <Label>Field Key (database field name)</Label>
                <Input
                  value={editFieldDialog.field.key}
                  onChange={(e) => setEditFieldDialog(prev => ({
                    ...prev,
                    field: { ...prev.field, key: e.target.value }
                  }))}
                  className="mt-2"
                  placeholder="e.g., fuel_economy"
                />
              </div>
              <div>
                <Label>Display Label</Label>
                <Input
                  value={editFieldDialog.field.label}
                  onChange={(e) => setEditFieldDialog(prev => ({
                    ...prev,
                    field: { ...prev.field, label: e.target.value }
                  }))}
                  className="mt-2"
                  placeholder="e.g., Better fuel economy"
                />
              </div>
              <div>
                <Label>Field Type</Label>
                <Select
                  value={editFieldDialog.field.type}
                  onValueChange={(value) => setEditFieldDialog(prev => ({
                    ...prev,
                    field: { ...prev.field, type: value }
                  }))}
                >
                  <SelectTrigger className="mt-2">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {FIELD_TYPES.map(type => (
                      <SelectItem key={type.value} value={type.value}>
                        {type.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditFieldDialog({ open: false, tabKey: null, fieldIndex: null, field: null })}>
              Cancel
            </Button>
            <Button onClick={handleUpdateField}>
              Update Field
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add Field Dialog */}
      <Dialog open={addFieldDialog.open} onOpenChange={(open) => !open && setAddFieldDialog({ open: false, tabKey: null })}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Add New Field</DialogTitle>
            <DialogDescription>Add a field to the {tabsConfig[addFieldDialog.tabKey]?.name} tab</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <Label>Field Key (database field name)</Label>
              <Input
                value={newField.key}
                onChange={(e) => setNewField(prev => ({ ...prev, key: e.target.value }))}
                className="mt-2"
                placeholder="e.g., warranty_months"
              />
              <p className="text-xs text-gray-500 mt-1">Use snake_case, must match database field</p>
            </div>
            <div>
              <Label>Display Label</Label>
              <Input
                value={newField.label}
                onChange={(e) => setNewField(prev => ({ ...prev, label: e.target.value }))}
                className="mt-2"
                placeholder="e.g., Warranty (months)"
              />
            </div>
            <div>
              <Label>Field Type</Label>
              <Select
                value={newField.type}
                onValueChange={(value) => setNewField(prev => ({ ...prev, type: value }))}
              >
                <SelectTrigger className="mt-2">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {FIELD_TYPES.map(type => (
                    <SelectItem key={type.value} value={type.value}>
                      {type.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setAddFieldDialog({ open: false, tabKey: null })}>
              Cancel
            </Button>
            <Button onClick={() => handleAddField(addFieldDialog.tabKey)}>
              <Plus className="h-4 w-4 mr-2" />
              Add Field
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ListingTabsManagementPage;
